import { useState, useEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface DatePickerProps {
  value?: string | null
  onChange: (date: string) => void
  onClose: () => void
}

export function DatePicker({ value, onChange, onClose }: DatePickerProps) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (value) {
      const d = new Date(value)
      return new Date(d.getFullYear(), d.getMonth(), 1)
    }
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  })
  
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate()
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay()
  
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
  }

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
  }

  const selectDate = (day: number) => {
    const selected = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
    if (selected < today) return // Can't select past dates
    
    const formatted = selected.toISOString().split('T')[0]
    onChange(formatted)
    onClose()
  }

  const isSelected = (day: number) => {
    if (!value) return false
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
    return date.toISOString().split('T')[0] === value
  }

  const isToday = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
    return date.toDateString() === today.toDateString()
  }

  const isPast = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
    return date < today
  }

  const days = []
  // Empty cells for days before the first of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(<div key={`empty-${i}`} className="w-8 h-8" />)
  }
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const past = isPast(day)
    const selected = isSelected(day)
    const todayClass = isToday(day)
    
    days.push(
      <button
        key={day}
        onClick={() => selectDate(day)}
        disabled={past}
        className={`
          w-8 h-8 rounded-full text-sm font-medium transition-all
          ${past ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-blue-100'}
          ${selected ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}
          ${todayClass && !selected ? 'ring-2 ring-blue-400 ring-offset-1' : ''}
        `}
      >
        {day}
      </button>
    )
  }

  const monthName = currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })

  return (
    <div 
      ref={ref}
      className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50 w-72"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button 
          onClick={prevMonth}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <span className="font-semibold text-gray-800">{monthName}</span>
        <button 
          onClick={nextMonth}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
          <div key={d} className="w-8 h-6 text-center text-xs font-medium text-gray-500">
            {d}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-1">
        {days}
      </div>

      {/* Quick select buttons */}
      <div className="mt-3 pt-3 border-t flex gap-2">
        <button
          onClick={() => {
            onChange(today.toISOString().split('T')[0])
            onClose()
          }}
          className="flex-1 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded"
        >
          Today
        </button>
        <button
          onClick={() => {
            const tomorrow = new Date(today)
            tomorrow.setDate(tomorrow.getDate() + 1)
            onChange(tomorrow.toISOString().split('T')[0])
            onClose()
          }}
          className="flex-1 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded"
        >
          Tomorrow
        </button>
        <button
          onClick={() => {
            const nextWeek = new Date(today)
            nextWeek.setDate(nextWeek.getDate() + 7)
            onChange(nextWeek.toISOString().split('T')[0])
            onClose()
          }}
          className="flex-1 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded"
        >
          Next Week
        </button>
      </div>
    </div>
  )
}
